import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Visibility, VisibilityOff } from '@mui/icons-material';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material';

import {
  dividerSx,
  fieldLabelSx,
  forgotLinksSx,
  forgotLinkSx,
  formControlSx,
  formWrapperSx,
  newPracticeSx,
  passwordIconButtonSx,
  registerLinkSx,
  requiredSx,
  signInButtonSx,
  signInSubtitleSx,
  signInTitleSx
} from '../../Common/components/Styles/AuthStyles';

const actionLinkSx = {
  ...forgotLinkSx,
  background: 'none',
  border: 0,
  cursor: 'pointer',
  padding: 0
};

const SignInForm = ({
  formState,
  hasError,
  onChange,
  onForgotPassword,
  onForgotUsername,
  onSubmit
}) => {
  const usernameId = React.useId();
  const passwordId = React.useId();

  const [showPassword, setShowPassword] = useState(false);

  const getErrorMessage = field => formState.errors[field]?.[0] || '';

  const handleTogglePassword = () => {
    setShowPassword(currentValue => !currentValue);
  };

  const handlePasswordMouseDown = event => {
    event.preventDefault();
  };

  return (
    <Box
  component="form"
  noValidate
  onSubmit={onSubmit}
  sx={formWrapperSx}>
  <Box>
    <Typography component="h2" sx={signInTitleSx}>
      Sign In
    </Typography>

    <Typography sx={signInSubtitleSx}>
      Access your provider portal.
    </Typography>
  </Box>

  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0
    }}>
    <Box>
      <Typography
        component="label"
        htmlFor={usernameId}
        sx={fieldLabelSx}>
        Username
        <Box component="span" sx={requiredSx}>
          *
        </Box>
      </Typography>

      <FormControl
        error={hasError('username')}
        fullWidth
        sx={formControlSx}
        variant="outlined">
        <OutlinedInput
          autoComplete="username"
          id={usernameId}
          name="username"
          onChange={onChange}
          placeholder="Enter your username"
          type="text"
          value={formState.values.username}
        />

        <FormHelperText
          sx={{
            minHeight: 16,
            visibility: hasError('username')
              ? 'visible'
              : 'hidden'
          }}>
          {getErrorMessage('username')}
        </FormHelperText>
      </FormControl>
    </Box>

    <Box>
      <Typography
        component="label"
        htmlFor={passwordId}
        sx={fieldLabelSx}>
        Password

        <Box component="span" sx={requiredSx}>
          *
        </Box>
      </Typography>

      <FormControl
        error={hasError('password')}
        fullWidth
        sx={formControlSx}
        variant="outlined">
        <OutlinedInput
          autoComplete="current-password"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label={
                  showPassword
                    ? 'Hide password'
                    : 'Show password'
                }
                edge="end"
                onClick={handleTogglePassword}
                onMouseDown={handlePasswordMouseDown}
                sx={passwordIconButtonSx}>
                {showPassword ? (
                  <VisibilityOff />
                ) : (
                  <Visibility />
                )}
              </IconButton>
            </InputAdornment>
          }
          id={passwordId}
          name="password"
          onChange={onChange}
          placeholder="Enter your password"
          type={showPassword ? 'text' : 'password'}
          value={formState.values.password}
        />

        <FormHelperText
          sx={{
            minHeight: 16,
            visibility: hasError('password')
              ? 'visible'
              : 'hidden'
          }}>
          {getErrorMessage('password')}
        </FormHelperText>
      </FormControl>
    </Box>
  </Box>

  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
    <Box sx={forgotLinksSx}>
      <Link
        component="button"
        onClick={onForgotUsername}
        sx={actionLinkSx}
        type="button">
        Forgot Username?
      </Link>

      <Typography component="span" sx={dividerSx}>
        |
      </Typography>

      <Link
        component="button"
        onClick={onForgotPassword}
        sx={actionLinkSx}
        type="button">
        Forgot Password?
      </Link>
    </Box>

    <Button
      fullWidth
      sx={signInButtonSx}
      type="submit"
      variant="contained">
      Sign In
    </Button>
  </Box>

  <Box
    sx={{
      alignItems: 'center',
      display: 'flex',
      gap: 1,
      my: 1.5
    }}>
    <Box
      sx={{
        bgcolor: '#E4E9F1',
        flex: 1,
        height: '1px'
      }}
    />

    <Typography
      sx={{
        color: '#8792A5',
        fontSize: 11,
        fontWeight: 500
      }}>
      or
    </Typography>

    <Box
      sx={{
        bgcolor: '#E4E9F1',
        flex: 1,
        height: '1px'
      }}
    />
  </Box>

  <Typography sx={newPracticeSx}>
    <Link
      component={RouterLink}
      sx={registerLinkSx}
      to="register-practice">
      Register a new practice
    </Link>
  </Typography>
</Box>
  );
};

SignInForm.propTypes = {
  formState: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    isValid: PropTypes.bool.isRequired,
    values: PropTypes.shape({
      password: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  hasError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onForgotPassword: PropTypes.func.isRequired,
  onForgotUsername: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default SignInForm;
